import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Loader2, Search, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BLOG } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = [
    "All",
    "Nigeria",
    "Kenya",
    "South Africa",
    "Mauritius",
    "Cameroon",
    "Ivory Coast",
    "Benin",
    "Tanzania",
    "Services",
    "Strategy",
];

const slugifyFilter = (s) => s.toLowerCase().replace(/\s+/g, "-");

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "";
    }
};

export default function Blog() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [active, setActive] = useState("All");
    const [query, setQuery] = useState("");

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await axios.get(`${API}/blog`);
                if (mounted) setArticles(res.data || []);
            } catch (e) {
                if (mounted) setError("Could not load the blog right now. Please try again.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const visible = useMemo(() => {
        let list = articles;
        if (active !== "All") {
            list = list.filter((a) => (a.category || "").toLowerCase() === active.toLowerCase());
        }
        const q = query.trim().toLowerCase();
        if (q) {
            list = list.filter((a) => {
                const hay = [a.title, a.excerpt, a.summary, a.body, a.category]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            });
        }
        return list;
    }, [articles, active, query]);

    return (
        <div data-testid={BLOG.section}>
            {/* HERO */}
            <section className="container-x pt-24 pb-12">
                <p className="overline mb-8">Chapter 04 · Blog</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.95] tracking-tight overflow-visible pr-2"
                        style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                    >
                        Notes from{" "}
                        <span className="text-gradient-blue italic inline-block pr-[0.12em] -mr-[0.02em]">
                            the desk.
                        </span>
                    </h1>
                </Reveal>
                <Reveal delay={120} className="mt-10 max-w-3xl">
                    <p className="text-xl text-brand-frost/85 leading-relaxed">
                        Short reads on growing financial and trading brands across Africa. Field notes, playbooks and the occasional opinion.
                    </p>
                </Reveal>
            </section>

            {/* SEARCH BAR */}
            <section className="border-t border-brand-primary/10 bg-brand-bg">
                <div className="container-x py-6">
                    <div className="relative flex items-center gap-3 border border-brand-primary/20 focus-within:border-brand-primary/60 transition-colors px-4">
                        <Search className="w-4 h-4 text-brand-primary/70 shrink-0" />
                        <input
                            data-testid="blog-search-input"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search articles."
                            className="flex-1 bg-transparent border-0 outline-none text-base text-brand-mist placeholder:text-brand-primary/50 py-3"
                        />
                        {query ? (
                            <button
                                data-testid="blog-search-clear"
                                onClick={() => setQuery("")}
                                className="shrink-0 p-1 text-brand-primary/70 hover:text-brand-mist transition-colors"
                                aria-label="Clear search"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        ) : null}
                    </div>
                </div>
            </section>

            {/* FILTER TABS */}
            <section className="border-y border-brand-primary/10 sticky top-20 z-30 bg-brand-bg/85 backdrop-blur-xl">
                <div className="container-x flex items-center gap-2 sm:gap-3 overflow-x-auto no-scrollbar py-5">
                    {FILTERS.map((f) => {
                        const isActive = active === f;
                        return (
                            <button
                                key={f}
                                data-testid={`blog-filter-${slugifyFilter(f)}`}
                                onClick={() => setActive(f)}
                                className={`shrink-0 px-4 sm:px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
                                    isActive
                                        ? "bg-brand-primary text-brand-bg border-brand-primary"
                                        : "border-brand-primary/30 text-brand-primary hover:text-brand-mist hover:border-brand-primary"
                                }`}
                            >
                                {f}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* GRID */}
            <section className="py-12 lg:py-16">
                <div className="container-x">
                    {loading ? (
                        <div className="flex items-center gap-3 text-brand-primary/70 py-20">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm uppercase tracking-widest">Loading articles</span>
                        </div>
                    ) : error ? (
                        <p className="text-brand-frost/70 py-20">{error}</p>
                    ) : visible.length === 0 ? (
                        <p data-testid="blog-no-results" className="text-brand-frost/70 py-20">
                            {query.trim()
                                ? `No results found for "${query}". Try a broader term or a different category.`
                                : "No articles in this category yet."}
                        </p>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-8">
                                <p className="overline">{visible.length} {visible.length === 1 ? "article" : "articles"}</p>
                                {active !== "All" && (
                                    <button
                                        onClick={() => setActive("All")}
                                        className="text-xs uppercase tracking-[0.2em] text-brand-primary hover:text-brand-mist transition-colors"
                                    >
                                        Clear filter
                                    </button>
                                )}
                            </div>
                            <div data-testid={BLOG.grid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {visible.map((a, i) => (
                                    <Reveal key={a.slug} delay={(i % 3) * 60}>
                                        <Link
                                            to={`/blog/${a.slug}`}
                                            data-testid={BLOG.card(a.slug)}
                                            className="group relative flex flex-col h-full border border-brand-primary/10 bg-brand-surface hover:border-brand-primary/40 transition-colors duration-500"
                                        >
                                            <div className="relative overflow-hidden">
                                                <div
                                                    className="aspect-[16/10] w-full bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
                                                    style={{ backgroundImage: `url(${a.cover_image})` }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/70 via-transparent to-transparent" />
                                                <span className="absolute top-4 left-4 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-brand-bg/80 backdrop-blur-sm text-brand-primary border border-brand-primary/30">
                                                    {a.category}
                                                </span>
                                            </div>
                                            <div className="flex flex-col flex-1 p-7 lg:p-8">
                                                <h2 className="font-display text-2xl lg:text-[26px] leading-tight tracking-tight text-brand-mist mb-3 transition-colors group-hover:text-brand-primary">
                                                    {a.title}
                                                </h2>
                                                <p className="text-brand-frost/75 leading-relaxed text-sm mb-6 flex-1">{a.excerpt}</p>
                                                <div className="flex items-center justify-between pt-5 border-t border-brand-primary/10">
                                                    <div className="flex items-center gap-3 text-xs text-brand-primary/70 uppercase tracking-widest">
                                                        <span>{formatDate(a.published_at)}</span>
                                                        <span className="text-brand-primary/40">·</span>
                                                        <span>{a.read_minutes} min read</span>
                                                    </div>
                                                    <ArrowUpRight className="w-4 h-4 text-brand-primary/60 transition-all duration-500 group-hover:text-brand-primary group-hover:translate-x-1 group-hover:-translate-y-1" />
                                                </div>
                                            </div>
                                        </Link>
                                    </Reveal>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
                    <Reveal>
                        <h2 className="font-display h-display-lg max-w-3xl">
                            Want us to write
                            <br />
                            <span className="italic text-brand-primary inline-block pr-[0.12em]">about your market?</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={120}>
                        <Link to="/contact" className="btn-primary">
                            Start the conversation <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
